import React from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import StarryBackground from '../animations/StarryBackground';
import FloatingShapes from '../animations/FloatingShapes';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'Staffing & Recruitment', href: '/services' },
      { name: 'Executive Search', href: '/services' },
      { name: 'Contract & Temporary Staffing', href: '/services' },
      { name: 'HR Consulting', href: '/services' },
    ],
    company: [
      { name: 'POSH Professionals', href: '/posh-professionals' },
      { name: 'Gallery', href: '/gallery' },
      { name: 'For Clients', href: '/for-clients' },
      { name: 'For Candidates', href: '/for-candidates' },
      { name: 'Careers', href: '/careers' },
      { name: 'Blog', href: '/blog' },
    ],
    support: [
      { name: 'Contact Us', href: '/contact' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Admin Login', href: '/login' },
    ],
  };

  return (
    <footer className="relative bg-[#020617] text-white overflow-hidden">
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-primary-900/20 blur-[120px] rounded-full" />
        <StarryBackground />
        <FloatingShapes />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 border-b border-gray-800/50 pb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center shadow-lg shadow-primary-500/20">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <span className="text-xl font-bold tracking-tight">Genexasource</span>
            </div>
            <p className="text-gray-400 text-sm mb-6 max-w-md leading-relaxed">
              Connecting talent with opportunity — faster, smarter, better.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3 group">
                <PhoneIcon className="h-5 w-5 text-primary-400 group-hover:scale-110 transition-transform" />
                <span className="text-gray-300 text-sm">+91-9611620183</span>
              </div>
              <div className="flex items-center space-x-3 group">
                <EnvelopeIcon className="h-5 w-5 text-primary-400 group-hover:scale-110 transition-transform" />
                <span className="text-gray-300 text-sm">info@genexasource.com</span>
              </div>
              <div className="flex items-start space-x-3 group">
                <MapPinIcon className="h-5 w-5 text-primary-400 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-gray-300 text-sm">Mount Joy Road, Hanumantanagar, Bangalore - 560019</span>
              </div>
            </div>
          </div>

          {/* Links Sections */}
          {[
            { title: 'Services', links: footerLinks.services },
            { title: 'Company', links: footerLinks.company },
            { title: 'Support', links: footerLinks.support }
          ].map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-bold uppercase tracking-widest text-primary-400 mb-5">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-white hover:translate-x-1 inline-block transition-all duration-200 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">
            © {currentYear} <span className="text-gray-300">Genexasource</span>. All rights reserved.
          </p>
          
          <div className="flex items-center space-x-6 mt-6 md:mt-0">
            {['LinkedIn', 'Twitter', 'Facebook'].map((social) => (
              <a
                key={social}
                href="/#"
                className="text-gray-500 hover:text-primary-400 transform hover:-translate-y-1 transition-all duration-200"
                aria-label={social}
              >
                <SocialIcon name={social} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ name }) => {
  if (name === 'LinkedIn') return <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" /></svg>;
  if (name === 'Twitter') return <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" /></svg>;
  return <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" /></svg>;
};

export default Footer;